// ============================================================
// llm_api.js — v2.0 (HR 고도화 버전)
// 역할: AI 보고서 생성 엔진
//   - VITE_GEMINI_API_KEY 환경변수가 있으면 → 실제 Gemini API 호출
//   - 없으면 → Mock 데이터로 즉시 응답 (데모/개발용)
// ============================================================

// ── 톤(문체)별 시스템 프롬프트 빌더 ──────────────────────────
// 비유: 같은 뉴스를 조선일보/한겨레/타임지 스타일로 쓰는 것처럼,
//       선택한 톤에 따라 AI에게 다른 문체 지침을 내립니다.
function buildSystemPrompt(tone = "samsung") {
  if (tone === "startup") {
    // 스타트업/IT식 — 실용적/Action 위주
    return `
당신은 실무 경험이 풍부한 HR 팀장입니다.
다음 원칙으로 팀 내부 공유용 보고서를 작성하십시오:

1. 핵심만: 불필요한 수식어 생략, 핵심 사항 불렛(●) 중심 작성.
2. 친근한 문체: "~합니다", "~됩니다" 등 자연스러운 경어체 사용.
3. 숫자 우선: 구체적 수치와 데이터로 근거 제시.
4. 실행 가능성: 즉시 실행할 수 있는 현실적 방안 위주.
5. 간결한 표현: 한 문장은 30자 이내로 간결하게.
`;
  } else if (tone === "global") {
    // 글로벌/영문식 — English BLUF
    return `
You are an experienced HR Business Partner with expertise in global corporate reporting.
Write a professional business report in English following these principles:

1. BLUF (Bottom Line Up Front): Lead with the conclusion and key recommendations.
2. Executive Summary: Start with a concise 3-5 sentence summary for C-Level readers.
3. Data-Driven: Use specific metrics, percentages, and financial figures throughout.
4. Structured Format: Use clear headings, numbered lists, and comparison tables.
5. Action-Oriented: End each section with clear next steps and owners.
6. Professional Tone: Formal business English, avoid jargon and colloquialisms.
`;
  } else if (tone === "bluehouse") {
    // 공공기관 (청와대식) — 정책적 당위성 강조
    return `
당신은 공공기관 혹은 국정 운영 최고 결정권자(대통령 등)에게 보고하는 전략기획 전문가입니다.
다음 원칙을 절대적으로 준수하여 보고서를 작성하십시오:

1. 명분과 당위성 중심: 사안이 왜 지금 중요한지, 국가적/사회적 파급 효과가 무엇인지 서두에 부각할 것.
2. 정제된 어휘: "검토 요망", "사료됨" 등 보수적이고 격식 있는 행정 용어를 사용할 것.
3. 다각적 リ스크 분석: 특정 부처뿐 아니라 이해관계자(시민, 지자체, 언론 등) 전반의 리스크와 여론 동향을 포함할 것.
4. 종합적 대안 제시: 단편적 조치보다는 종합적이고 포괄적인 정책 대안(플랜 A/B/C)을 기조로 작성.
5. 단호하되 신중한 결론: 최종 건의 사항은 무게감 있게 전달하되, 실행 과정에서의 부작용 최소화 방안을 명시할 것.
`;
  } else {
    // samsung — 대기업식 (기본값)
    return `
당신은 최고 수준의 C-Level 전략기획 보고서 전문 작성 AI입니다.
다음 원칙을 절대적으로 준수하여 보고서를 작성하십시오:

1. BLUF (Bottom Line Up Front): 결론·핵심 요청 사항을 문서 최상단에 배치할 것.
2. 개조식 문체: "~임", "~함", "~예정" 등 간결한 종결 어미 사용. 산문체 금지.
3. 숫자 우선주의: 모호한 표현("최선", "노력") 배제. 검증 가능한 수치·데이터로 대체.
4. MECE 구조: 상호 배타적이고 누락 없는 논리 구성.
5. 사실(Fact)·의견(Opinion) 분리: 객관적 사실과 보고자 의견을 명확히 구분.
6. 임원 관점: 독자는 바쁜 임원이므로, 핵심은 표(비교표·액션플랜표)로 직관적 표현.
7. 액션 플랜(과제): 핵심 실행 과제(Action Items)를 작성할 때는 반드시 "No." 연번 컬럼을 포함하거나 과제명 앞에 번호(1., 2. ...)를 붙여 넘버링을 명시할 것.
`;
  }
}

// 하위 호환성을 위해 기본 SYSTEM_PROMPT도 유지
const SYSTEM_PROMPT = buildSystemPrompt("samsung");

// ── Mock 응답 데이터 (시나리오별 샘플 보고서) ─────────────────
const MOCK_RESPONSES = {

  // ── 인력 충원 요청 ────────────────────────────────────────
  Headcount_Request: {
    reportTitle: "데이터분석팀 전문인력 2명 충원 승인 요청",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론 및 요청]** 데이터분석팀 전문인력 2명 즉시 충원 승인 요청 (총 소요 예산: 연 1.6억 원)

**[핵심 근거]**
- 현 팀 업무 포화율: 142% (주간 평균 업무시간 71시간/인)
- 기존 인력 흡수 불가: 3개 신규 AI 프로젝트 동시 진행 확정으로 추가 공수 불가
- 충원 미시 리스크: Q3 사업보고서 분석 납기 지연 → 이사회 의사결정 불가 상황 예상

**[의사결정 옵션]**
| 옵션 | 내용 | 비용 | 리스크 |
|------|------|------|--------|
| **A안 (권고)** | 정규직 2명 즉시 채용 | 연 1.6억 원 | 最低 |
| B안 | 프리랜서 2명 단기 활용 | 월 800만 원 | 기밀 유출 우려 |
| C안 | 충원 없이 현상 유지 | 0원 | 납기 지연·번아웃 高 |

→ **A안 승인 요청**`,
      },
      {
        id: "AS_IS_TO_BE", title: "현황 및 목표 상태",
        content: `**[AS-IS 현황]**
- 팀 구성: 팀장 1명 + 분석가 3명 = 총 4명
- 주당 평균 업무 시간: 71시간/인 (법정 기준 52시간 대비 +19시간 초과)
- Q2 납기 준수율: 67% (3건 중 2건 기한 초과)
- 팀원 번아웃 지수: 3.8/5.0 (위험 수준)

**[TO-BE 목표]**
- 팀 구성: 팀장 1명 + 분석가 5명 = 총 6명 (+2명)
- 주당 평균 업무 시간: 50시간/인 이하 (법정 기준 준수)
- 납기 준수율: 95% 이상
- 팀원 번아웃 지수: 2.5 이하 (안정 수준)`,
        asIsData: {
          label: "팀 인원 구성",
          asList: ["팀장 1명", "분석가 3명", "총 4명"],
          toBeList: ["팀장 1명", "분석가 5명", "총 6명 (+2명 충원)"],
        },
      },
      {
        id: "Financial_Impact", title: "소요 예산 및 재무적 영향",
        content: `**[충원 소요 비용]**
- 연봉: 1인당 7,000만 원 × 2명 = 1억 4,000만 원/년
- 채용 비용: 헤드헌터 수수료 약 1,200만 원 (1회성)
- 온보딩 비용: 교육·장비 약 400만 원
- **1년차 총 비용: 약 1억 5,600만 원**

**[비충원 시 기회비용]**
- Q3 프로젝트 납기 지연 시 위약금 추정: 5,000만 원
- 팀원 번아웃 → 자발적 이직 1명 발생 시 대체 비용: 약 8,000만 원
- **비충원 리스크 비용 합계: 약 1억 3,000만 원**`,
      },
      {
        id: "Expected_ROI", title: "기대 효과 및 ROI",
        content: `**[정량적 기대 효과]**
- 납기 준수율 67% → 95%+ 개선 (Q3부터 즉시 적용)
- 연간 데이터 분석 처리 건수: 180건 → 280건 (+56%)
- 야근 비용(OT 수당) 절감: 연 약 3,600만 원

**[정성적 기대 효과]**
- 팀원 번아웃 해소 → 핵심인재 유지
- 이사회·경영진 데이터 기반 의사결정 속도 향상
- AI 프로젝트 3건 정상 일정 추진 가능

**[ROI 요약]** 투자비용 1.56억 원 / 기대 절감·창출 효과 2.7억 원+ → **BEP 달성 예상: 충원 후 8개월**`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 단계 | 내용 | 담당 | 기한 |
|------|------|------|------|
| 1 | 채용 공고 오픈 (헤드헌터 동시 의뢰) | HR팀 | D+1주 |
| 2 | 서류 심사 및 1차 면접 | 팀장, HR | D+3주 |
| 3 | 임원 면접 및 최종 합격 | 임원, HR | D+4주 |
| 4 | 처우 협의 및 입사 확정 | HR | D+5주 |
| 5 | 온보딩 및 업무 배치 | 팀장 | D+7~8주 |

→ **목표 투입 시점: 2026년 6월 초 (Q3 프로젝트 킥오프 전)**`,
      },
    ],
  },

  // ── 성과 평가 / KPI 결과 보고 ─────────────────────────────
  Performance_Review: {
    reportTitle: "2026년 상반기 전사 KPI 성과 평가 결과 보고",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 2026년 상반기 전사 KPI 달성률 평균 **78%** — 목표(100%) 대비 -22%p 미달

**[핵심 현황]**
- 초과 달성 팀: 영업1팀(131%), 마케팅팀(108%) → 목표 상향 검토 필요
- 심각한 미달 팀: R&D팀(59%), 생산팀(63%) → 구조적 지원 시급
- 전사 S/A등급 비율: 24% (전기 31% → -7%p 하락, 3년 래 최저)

**[의사결정 요청]**
1. R&D팀 인원 2명 긴급 충원 승인 (별도 품의 예정)
2. 하위 10% (C등급 이하 12명) 대상 PIP 시행 승인
3. 차기 목표치 재설정 기준 승인 (협의안 첨부)`,
      },
      {
        id: "Performance_Analysis", title: "성과 현황 분석",
        content: `**[팀별 KPI 달성률]**
| 팀 | 목표 | 달성률 | 등급 | 특이사항 |
|----|------|--------|------|---------|
| 영업1팀 | 매출 100억 | 131% | S | 신규 거래처 5건 확보 |
| 마케팅팀 | 리드 1,000건 | 108% | A | SNS 신규 채널 효과 |
| 경영지원팀 | 원가절감 3% | 95% | B | 정상 범위 |
| 생산팀 | 가동률 90% | 63% | C | 설비 노후화로 고장 빈발 |
| R&D팀 | 특허 3건 | 59% | C | 인원 부족으로 과제 지연 |

**[등급 분포]**
- S(120%+): 1팀 / A(100%+): 2팀 / B(80%+): 5팀 / C(~79%): 2팀`,
      },
      {
        id: "AS_IS_TO_BE", title: "전기 대비 성과 변화",
        content: `**[전기 대비 주요 변화]**

| 구분 | 2025년 하반기 | 2026년 상반기 | 증감 |
|------|-------------|-------------|------|
| 전사 평균 달성률 | 89% | 78% | **-11%p** ↓ |
| S등급 비율 | 31% | 24% | -7%p ↓ |
| C등급 이하 비율 | 8% | 17% | **+9%p** ↑ |
| 목표 달성 팀 수 | 8팀/10팀 | 6팀/10팀 | -2팀 ↓ |

**[하락 원인 진단]**
- 하반기 신규 목표치 과도한 상향 조정 (평균 +18% 상향)
- R&D/생산팀 구조적 문제 미해결로 누적 저하`,
      },
      {
        id: "Risk_Mitigation", title: "리스크 및 대응 방안",
        content: `**[주요 리스크]**
- R&D팀 C등급 지속 시: 핵심 특허 파이프라인 고갈 → 3년 내 신사업 동력 상실
- S등급 인재 이탈 우려: 영업1팀 초과 성과자 2명의 타사 영입 제안 확인됨
- PIP 대상자 반발 시 노무 리스크 발생 가능성

**[대응 방안]**
| 리스크 | 대응 방안 | 담당 | 기한 |
|--------|---------|------|------|
| R&D 저성과 | 인원 2명 긴급 충원 + 목표 재조정 | HR, R&D팀장 | 7월 말 |
| 핵심인재 이탈 | 스팟 리텐션 인센티브 지급 검토 | HR, 경영진 | 이번 달 |
| PIP 갈등 | 개별 면담 선행, 법무 검토 후 시행 | HR, 법무 | 7월 초 |`,
      },
      {
        id: "Action_Plan", title: "핵심 실행 과제 (Action Items)",
        content: `| No. | 과제 | 담당자 | 기한 | 예산 |
|-----|------|--------|------|------|
| 1 | PIP 대상 12명 면담 및 개선 목표 확정 | HR팀장 | 7/15 | - |
| 2 | R&D팀 인원 충원 품의 상신 | HR팀, R&D팀장 | 7/10 | 1.4억/년 |
| 3 | 영업1팀 핵심 2명 리텐션 인센티브 협의 | HR팀장, CEO | 7/5 | 미정 |
| 4 | 하반기 목표치 재조정안 작성 및 승인 | 전략기획 | 7/31 | - |
| 5 | 생산팀 설비 투자 타당성 검토 의뢰 | 생산팀장, 재무 | 8/15 | 검토 중 |`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 마일스톤 |
|------|---------|
| 7월 1주차 | PIP 면담 시작, 핵심인재 리텐션 협의 |
| 7월 2주차 | 충원 품의 상신, 하반기 목표 재설정 작업 착수 |
| 7월 말 | PIP 계획 확정, 하반기 목표 경영진 승인 |
| 8월 중 | 하반기 킥오프, 신규 채용 1차 면접 |
| 9월 말 | 신규 인원 온보딩 완료, 3분기 중간 점검 |`,
      },
    ],
  },

  // ── 이직율 분석 / 리텐션 대책 ─────────────────────────────
  Attrition_Analysis: {
    reportTitle: "2026년 상반기 이직율 급등 원인 분석 및 리텐션 대책 보고",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 상반기 자발적 이직율 **18%** — 전년 동기(11%) 대비 +7%p 급등. 즉각적 개입 필요.

**[핵심 수치]**
- 이탈 인원: 상반기 34명 (전년 동기 21명)
- 주요 이탈 그룹: 2~4년차 대리급(68%), 기술직(조기이탈率 전체의 56%)
- 연간 이직 비용 추산: **약 17억 원** (대체 채용·교육·생산성 손실 포함)

**[의사결정 요청]**
1. 리텐션 긴급 패키지(연봉 조정 + 복지 확대) 예산 **5억 원 승인** 요청
2. 이직율 목표치: 연말까지 **12% 이하** 달성 승인 요청`,
      },
      {
        id: "Root_Cause_Analysis", title: "근본 원인 분석",
        content: `**[퇴직 인터뷰 분석 결과 (34명 기준)]**
| 이직 원인 | 응답 비율 | 비고 |
|----------|---------|------|
| 1. 연봉 불만 (시장가 대비 낮음) | 71% | 시장 대비 평균 -18% |
| 2. 성장 / 커리어 기회 부재 | 65% | 승진 적체 2~3년 |
| 3. 직속 관리자와의 갈등 | 41% | 특정 본부 집중 |
| 4. 야근·업무 과부하 | 38% | 주 60시간+ |
| 5. 회사 방향성에 대한 불신 | 29% | M&A 루머 영향 추정 |

**[근본 원인 진단]**
- 표면: 연봉 불만
- 근본: 보상 체계의 시장 연동 부재 (3년 이상 업데이트 없음) + 명확한 승진 기준 미정립
- 특정 관리자 리더십 문제 → 해당 팀 이직율 37% (전사 평균의 2배)`,
      },
      {
        id: "Financial_Impact", title: "재무적 영향",
        content: `**[이직 비용 산출]**
- 1인당 대체 비용 = 채용비 + 교육비 + 온보딩 생산성 손실
  - 채용비: 약 1,500만 원/인 (헤드헌터 포함)
  - 교육·온보딩: 약 500만 원/인
  - 생산성 손실(6개월): 약 2,000만 원/인
  - **1인당 합계: 약 4,000만 원**

- **상반기 이직 34명 기준: 약 13.6억 원 손실**
- 현 추세 지속 시 연간 손실 추산: **약 27억 원**

**[리텐션 투자 대비 효과]**
- 리텐션 패키지 예산: 5억 원
- 이직율 18% → 12%로 감소 시: 연간 약 14억 원 이직 비용 절감
- **ROI: 투자 대비 약 2.8배 절감 효과**`,
      },
      {
        id: "Risk_Mitigation", title: "리스크 및 대응 방안",
        content: `**[이탈 계속 시 리스크]**
- 기술직 이탈 가속 → 핵심 프로젝트 실행 동력 상실
- 남은 직원 업무 부담 증가 → 추가 이탈 연쇄 반응 ('이직 도미노')
- 채용 시장에 부정적 고용주 브랜드 형성

**[리스크별 대응 방안]**
| 리스크 | 대응 | 기한 |
|--------|------|------|
| 연봉 불만 | 시장가 조사 후 하위 분위 조정 | 8월 |
| 관리자 갈등 | 문제 관리자 1:1 리더십 코칭 | 즉시 |
| M&A 불안 | 경영진 타운홀 미팅 개최 | 이달 |
| 이직 도미노 | 핵심인재 Stay 인터뷰 실시 | 7월 |`,
      },
      {
        id: "Action_Plan", title: "핵심 실행 과제 (Action Items)",
        content: `| 우선순위 | 과제 | 예산 | 담당 | 기한 |
|---------|------|------|------|------|
| ★★★ | 연봉 시장가 조정 (하위 분위 대상 집중) | 3.5억 원 | HR, CFO | 8/1 시행 |
| ★★★ | 문제 관리자 리더십 개선 프로그램 | 0.2억 원 | HR | 즉시 |
| ★★ | 커리어 패스 로드맵 명문화 및 공유 | 0.1억 원 | HR, 팀장 | 7/31 |
| ★★ | 경영진 타운홀 미팅 (전사 방향성 공유) | - | CEO, 경영전략 | 7/15 |
| ★ | 핵심인재 Stay 인터뷰 (50명) | - | HR팀 | 7/31 |
| ★ | 재직자 추천 채용 보너스 강화 | 0.2억 원 | HR | 즉시 |`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 마일스톤 | 목표 지표 |
|------|---------|---------|
| 7월 1-2주 | 타운홀 미팅, 관리자 코칭 시작, Stay 인터뷰 | 이직 의향 확인 |
| 7월 3-4주 | 연봉 시장가 조사 완료, 조정안 확정 | 조정 대상 확정 |
| 8월 1일 | 연봉 조정 시행 | 직원 체감 즉시 |
| 8월 말 | 리텐션 패키지 효과 중간 점검 | 이직 예고 감소율 |
| 12월 말 | 연간 이직율 목표 12% 달성 확인 | **목표: 12% 이하** |`,
      },
    ],
  },

  // ── 핵심인재 육성 / 승계 계획 ────────────────────────────
  Talent_Management: {
    reportTitle: "2026년 핵심인재 현황 및 리더십 승계 계획 보고",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 임원급 승계 후보 공백 심각 — 주요 포지션 4개 중 2개의 Ready-Now 후보 부재

**[핵심 현황]**
- 전사 HP(High-Potential) 인재: 총 24명 (전체 대비 4.8%)
- 임원 Ready-Now 후보: 5개 포지션 중 **3개 공백** → 갑작스런 공석 시 사업 연속성 위기
- HP 인재 이탈 리스크 High: 24명 중 **7명** (29%) 이탈 징후 감지

**[의사결정 요청]**
1. 긴급 리텐션 대상 7명에 대한 스팟 인센티브 **1.2억 원 승인**
2. 리더십 파이프라인 강화 예산 **연 2억 원** 별도 편성 승인`,
      },
      {
        id: "Talent_Inventory", title: "핵심인재 현황 및 승계 계획",
        content: `**[HP 인재 현황]**
| 등급 | 인원 | 설명 |
|------|------|------|
| Ready-Now (즉시 투입) | 5명 | 현 포지션 +1레벨 즉시 가능 |
| Developing (1~2년 내) | 11명 | 추가 육성 필요 |
| Watch (잠재력 보유) | 8명 | 모니터링 단계 |

**[핵심 포지션별 승계 현황]**
| 포지션 | Ready-Now | Developing | 위험도 |
|--------|-----------|------------|--------|
| CTO | **공백** | 1명 | 🔴 HIGH |
| CSO | **공백** | 2명 | 🔴 HIGH |
| CFO | A부장 | 1명 | 🟡 MEDIUM |
| CHRO | B이사 | 1명 | 🟢 LOW |
| 영업본부장 | **공백** | 2명 | 🔴 HIGH |`,
      },
      {
        id: "Development_Roadmap", title: "역량 개발 로드맵",
        content: `**[단기 긴급 조치 (3개월 내)]**
- CTO 공백: 外部 임원급 서치 즉시 착수 (내부 육성 2년 필요 — 즉시 해결 불가)
- Developing 후보 3명: 임원 멘토링 프로그램 즉시 배정

**[중기 육성 계획 (1년)]**
| 프로그램 | 대상 | 규모 | 예산 |
|---------|------|------|------|
| 임원 Shadow 트레이닝 | Ready-Now 5명 | 6개월 | 0.3억 |
| 外 임원 코칭 프로그램 | Developing 11명 | 1년 | 0.8억 |
| 해외 벤치마킹 연수 | HP 상위 5명 | 2주 | 0.5억 |
| 전략 프로젝트 TF 배치 | Watch 8명 | 6개월 | 0.4억 |

→ **총 육성 예산: 연 2억 원**`,
      },
      {
        id: "Risk_Mitigation", title: "리스크 및 대응 방안",
        content: `**[핵심 리스크]**
- 이탈 위험 7명 중 1명이라도 이탈 시: 2개 팀 이상 운영 동력 즉각 약화
- CTO 공석 長期化: 기술 전략 공백 → 디지털 전환 로드맵 지연

**[이탈 위험 7명 긴급 대응 계획]**
| 대상 | 이탈 원인 추정 | 대응 방안 | 기한 |
|------|-------------|---------|------|
| C팀장 외 2명 | 연봉 시장가 괴리 | 스팟 인센티브 (인당 1,500~2,000만 원) | 즉시 |
| D주임 외 2명 | 성장기회 부재 | 임원 후보 트랙 편입 & 공식 고지 | 1개월 |
| E차장 외 1명 | 조직 불만 | CHRO 1:1 경력 상담 | 2주 |`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 마일스톤 |
|------|---------|
| 즉시 | 이탈 위험 7명 1:1 면담, 스팟 인센티브 집행 |
| 1개월 | CTO 外部 서치 착수, 임원 멘토링 배정 |
| 3개월 | 해외 연수 대상 확정, Shadow 트레이닝 시작 |
| 6개월 | 중간 평가 (Ready-Now 후보 升格 검토) |
| 12개월 | 승계 공백 포지션 해소 목표 확인 |`,
      },
    ],
  },

  // ── 급여 / 보상 체계 개편 ────────────────────────────────
  Compensation_Review: {
    reportTitle: "2026년 보상 체계 전면 개편안 보고 (직급별 호봉제 → 역할 기반 Pay Band)",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 현 호봉제 체계의 구조적 한계 봉착 → 역할 기반(Job Grade) 체계로 전환 승인 요청

**[개편 핵심]**
- 현황: 시장 대비 평균 연봉 -18% 저평가, 직급 간 역전 현상 15건 발생
- 개편 방향: 5직급 호봉제 → 3밴드(Junior/Senior/Lead) 역할 기반 Pay Band 체계
- 추가 인건비: 연 약 **3.2억 원** (이직 비용 절감 효과 14억 원으로 상쇄)

**[의사결정 요청]**
1. 개편안 채택 및 2027년 1월 시행 승인
2. 2026년 하반기 준비 예산 **0.5억 원 승인** (컨설팅·IT 시스템 개편)`,
      },
      {
        id: "AS_IS_TO_BE", title: "현황 및 목표 상태",
        content: `**[AS-IS] 현행 5직급 호봉제**
- 직급: 사원→대리→과장→차장→부장 (5단계)
- 급여 결정: 입사 연도 + 근속 연수 기반 자동 호봉 승급
- 문제점: ① 성과와 무관한 자동 인상 ② 시장가 미반영 ③ 역전 현상

**[TO-BE] 역할 기반 3-Band 체계**
- Band: Junior(1~3년) / Senior(4~7년) / Lead(8년+) (3단계)
- 급여 결정: 역할 복잡도 + 시장가(P50) + 성과 등급 연동
- 성과급: 기본급 대비 최대 30%(현행 15%) 으로 확대

| 구분 | AS-IS | TO-BE |
|------|-------|-------|
| 직급 수 | 5단계 | 3밴드 |
| 인상 기준 | 연공 자동 | 역할·성과 |
| 시장가 연동 | ❌ | ✅ 연 1회 조정 |
| 성과급 비중 | 최대 15% | 최대 30% |`,
        asIsData: {
          label: "보상 체계",
          asList: ["5직급 호봉제", "연공 자동 인상", "성과급 최대 15%", "시장가 미연동"],
          toBeList: ["3밴드 역할 기반", "역할·성과 연동 인상", "성과급 최대 30%", "시장가 연 1회 조정"],
        },
      },
      {
        id: "Financial_Impact", title: "소요 예산 및 재무적 영향",
        content: `**[추가 인건비 분석]**
- 시장가 정렬(P50 수준): 하위 분위 인원 약 80명 대상 → 연 약 2.4억 원
- Pay Band Range 확대 (Lead 밴드 상한 인상): 연 약 0.8억 원
- **총 추가 인건비: 연 약 3.2억 원**

**[절감 효과]**
- 이직율 18% → 13% 하락 예상 시 이직 비용 절감: **연 약 10억 원**
- 성과 연동 강화로 저성과자 자연 감소 효과 예상

**[준비 예산 (1회성)]**
- 보상 컨설팅: 0.3억 원
- HR IT 시스템 개편: 0.2억 원`,
      },
      {
        id: "Risk_Mitigation", title: "리스크 및 대응 방안",
        content: `**[예상 리스크]**
- 기존 고참 직원(호봉으로 수혜자) 불만 및 반발
- 새 체계 이해 부족으로 인한 혼란
- 노동법 검토 필요 (근로조건 불이익 변경 여부)

**[리스크 대응]**
| 리스크 | 대응 | 담당 |
|--------|------|------|
| 기득권 반발 | Grandfathering 조항 (기존 연봉 보호, Band 내 하향 없음) | HR, 법무 |
| 직원 혼란 | 부서별 설명회 5회 + FAQ 공지 | HR |
| 법적 분쟁 | 노무사 법적 검토 선행 (취업규칙 변경 동의 절차) | 법무, HR |`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 마일스톤 |
|------|---------|
| 2026년 7~8월 | 보상 컨설팅 진행, 시장 데이터 수집 |
| 2026년 9월 | Pay Band 설계 완료, 노무 법적 검토 |
| 2026년 10월 | 직원 설명회, 동의 절차 진행 |
| 2026년 11월 | 취업규칙 개정, HR 시스템 세팅 |
| 2027년 1월 | **신 보상 체계 공식 시행** |`,
      },
    ],
  },

  // ── 인건비 / HR 예산 분석 ────────────────────────────────
  Labor_Cost_Analysis: {
    reportTitle: "2026년 상반기 인건비 현황 분석 및 하반기 관리 방안",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 상반기 인건비 예산 초과 집행 (예산 대비 **+6.3%**) — 하반기 통제 필요

**[핵심 수치]**
- 상반기 인건비: 실집행 **87억 원** (예산 82억 원, 초과 5억 원)
- 매출 대비 인건비 비율: **29.3%** (업종 평균 24%, +5.3%p 과다)
- 주요 초과 원인: OT 수당 급증 (+2.1억), 긴급 채용 3건 (+1.8억), 연봉 인상 효과 (+1.1억)

**[의사결정 요청]**
1. 하반기 OT 한도 제도 도입 승인 (월 40시간 상한)
2. 신규 채용 하반기 동결 (기승인 건 제외) 승인`,
      },
      {
        id: "Financial_Impact", title: "인건비 상세 현황",
        content: `**[인건비 구성 분석]**
| 항목 | 예산 | 실적 | 差異 | 주요 원인 |
|------|------|------|------|---------|
| 기본급 | 62억 | 63.1억 | +1.1억 | 연봉 인상 효과 |
| 성과급/인센티브 | 10억 | 9.8억 | -0.2억 | 성과 미달 |
| OT 수당 | 3억 | 5.1억 | **+2.1억** | R&D, 생산팀 집중 |
| 채용 비용 | 3억 | 4.8억 | **+1.8억** | 긴급 경력 채용 3건 |
| 기타 | 4억 | 4.2억 | +0.2억 | - |
| **합계** | **82억** | **87억** | **+5억** | |

**[매출 대비 인건비 추이]**
- 2024년: 25.1% → 2025년: 27.4% → 2026년 상반기: 29.3% → **지속 악화**`,
      },
      {
        id: "AS_IS_TO_BE", title: "현황 및 목표",
        content: `| 지표 | 현재 (AS-IS) | 목표 (TO-BE) | 달성 기한 |
|------|------------|------------|---------|
| 매출 대비 인건비 | 29.3% | 25% 이하 | 2026년 말 |
| OT 수당 | 월 평균 0.85억 | 0.5억 이하 | 하반기 즉시 |
| 1인당 인건비 | 전년 대비 +9% | +5% 이내 유지 | 연말까지 |
| 신규 채용 | 상반기 12명 | 하반기 3명 이내 | 승인 시 즉시 |`,
        asIsData: {
          label: "인건비 비율",
          asList: ["매출 대비 29.3%", "OT 수당 월 0.85억", "상반기 신규채용 12명"],
          toBeList: ["매출 대비 25% 이하", "OT 수당 월 0.5억 이하", "하반기 신규채용 3명 이내"],
        },
      },
      {
        id: "Action_Plan", title: "핵심 실행 과제 (Action Items)",
        content: `| No. | 과제 | 기대 효과 | 담당 | 기한 |
|-----|------|---------|------|------|
| 1 | OT 월 40시간 상한 제도 도입 | 수당 약 1.2억/반기 절감 | HR, 각 팀장 | 8/1 |
| 2 | 하반기 채용 동결 (기승인 3건 제외) | 채용비 약 0.9억 절감 | HR | 즉시 |
| 3 | 유휴 인력 부서간 재배치 (5명) | 신규 채용 필요성 ↓ | HR, 각 본부장 | 7/31 |
| 4 | 정기 인건비 모니터링 (월 1회 보고) | 조기 이상 감지 | HR, CFO | 8월~ |`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 마일스톤 | 목표 |
|------|---------|------|
| 7월 말 | OT 한도 제도 설계 완료, 사전 공지 | - |
| 8월 1일 | OT 한도 시행, 채용 동결 공식화 | - |
| 8월 말 | 인력 재배치 완료 | -5명 신규채용 대체 |
| 9월 말 | 3분기 인건비 중간 점검 | 목표 궤도 확인 |
| 12월 말 | 연간 매출 대비 인건비 25% 달성 | **핵심 목표** |`,
      },
    ],
  },

  // ── 교육 / 역량 개발 투자 계획 ───────────────────────────
  Training_Development: {
    reportTitle: "2026년 하반기 디지털 역량 강화 교육 투자 계획 승인 요청",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 전사 디지털 리터러시 강화 교육 프로그램 승인 요청 — 총 투자액 **2.3억 원**

**[배경 및 필요성]**
- AI 도입 가속화에도 실무 활용 가능 인력 비율: 전사의 **18%** 수준 (목표 60%)
- 직원 역량 개발 요구도: 설문 응답자의 **73%** 가 디지털 교육 필요성 인식
- 경쟁사 平均 1인당 연 교육비: **85만 원** → 당사 현재: **28만 원** (3배 격차)

**[의사결정 요청]**
1. 하반기 디지털 역량 강화 예산 **2.3억 원 승인**
2. 교육 성과 KPI 설정 및 성과 연동 평가 도입 승인`,
      },
      {
        id: "Development_Roadmap", title: "역량 개발 로드맵",
        content: `**[교육 프로그램 구성]**
| 과정 | 대상 | 방식 | 규모 | 비용 |
|------|------|------|------|------|
| AI 기초 리터러시 | 전 직원 500명 | 온라인 (자기주도) | 필수 | 0.5억 |
| 데이터 분석 실무 | 현업 담당자 100명 | 집합+온라인 혼합 | 선택 | 0.8억 |
| AI 활용 심화 워크숍 | 팀장급 50명 | 2일 집합 | 필수 | 0.5억 |
| 외부 자격증 취득 지원 | 지원자 30명 | 자율 학습 | 선택 | 0.3억 |
| 러닝랩 플랫폼 구축 | 전 직원 | 상시 | 인프라 | 0.2억 |
| **합계** | | | | **2.3억** |

**[역량 수준 로드맵]**
- 7~8월: 기초 리터러시 전 직원 이수 (온라인)
- 9~10월: 데이터 분석 실무 과정 집중 운영
- 11~12월: 심화 워크숍 + 자격증 취득 지원`,
      },
      {
        id: "Financial_Impact", title: "소요 예산 및 재무적 영향",
        content: `**[투자 규모]**
- 총 교육 투자: **2.3억 원**
- 1인당 교육비: 평균 **46만 원** (현재 28만 원의 1.6배)

**[ROI 산출 기준]**
- 생산성 향상: AI 도구 활용으로 반복 업무 평균 20% 절감
  - 연간 인건비 절감 환산: 약 **5억 원** (500명 × 연봉 3,000만 원 × 20% × 일부)
- 채용 대체 효과: 외부 AI 인재 채용 불필요한 포지션 +3명 감소 → 약 **1.2억 원**
- **예상 ROI: 투자 대비 약 2.7배 (1년 내)**`,
      },
      {
        id: "Expected_ROI", title: "기대 효과 및 ROI",
        content: `**[정량적 목표]**
| KPI | 현재 | 목표 (연말) |
|-----|------|------------|
| AI 활용 가능 인력 비율 | 18% | 60%+ |
| 업무 자동화 적용 프로세스 수 | 3건 | 15건 |
| 1인당 교육 이수 시간 | 평균 12h/년 | 40h/년 |
| 교육 만족도 | 미측정 | 4.0/5.0 |
| 자격증 취득자 | 0명 | 30명 |

**[정성적 기대 효과]**
- 직원 성장 욕구 충족 → 이직율 하락 기여
- AI 도입 프로젝트 내재화 역량 확보 → 외부 의존도 감소`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 마일스톤 |
|------|---------|
| 7월 | 플랫폼 세팅, 콘텐츠 수급, 강사 섭외 |
| 8월 | 온라인 기초 과정 전 직원 순차 수강 시작 |
| 9~10월 | 데이터 분석 실무 집합 과정 운영 (분기별) |
| 11월 | 팀장급 AI 심화 워크숍 |
| 12월 | 성과 측정, KPI 달성 여부 보고, 내년 계획 수립 |`,
      },
    ],
  },

  // ── 직원 만족도 조사 결과 ─────────────────────────────────
  Employee_Satisfaction: {
    reportTitle: "2026년 상반기 직원 만족도(eNPS) 조사 결과 및 개선 계획 보고",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** eNPS **+18점** (전기 +34점 → **△16점 급락**) — 구조적 개입 없이 자연 회복 불가

**[핵심 수치]**
- 전사 eNPS: +18점 (업종 평균 +42점, 하위 20% 수준)
- 추천 의향: 32% / 중립: 54% / 비추천: 14%
- 응답률: 81% (신뢰도 확보)

**[Bottom 3 만족도 영역]**
1. 성장 기회 (2.5/5점 — 최하위)
2. 보상 공정성 (2.7점)
3. 워크-라이프 밸런스 (2.9점)

**[의사결정 요청]**
1. 긴급 개선 액션 플랜 추진 예산 **1.8억 원 승인**
2. 분기별 pulse 조사 도입 승인 (조기 경보 체계)`,
      },
      {
        id: "Performance_Analysis", title: "만족도 상세 현황",
        content: `**[영역별 만족도 점수]**
| 영역 | 점수 | 전기 대비 | 등급 |
|------|------|---------|------|
| 직무 의미 | 3.8 | +0.1 | 🟢 양호 |
| 동료 관계 | 3.7 | -0.1 | 🟢 양호 |
| 복리후생 | 3.3 | -0.2 | 🟡 보통 |
| 워크라이프밸런스 | 2.9 | -0.5 | 🟠 주의 |
| 보상 공정성 | 2.7 | -0.6 | 🔴 위험 |
| 성장 기회 | 2.5 | -0.8 | 🔴 위험 |

**[이탈 위험 세그먼트]**
- 2~4년차 대리급: eNPS -8점 (전사에서 유일한 마이너스)
- 기술직군: eNPS +3점 (전사 평균 대비 -15점)`,
      },
      {
        id: "Root_Cause_Analysis", title: "근본 원인 분석",
        content: `**[성장기회 최저의 원인]**
- 승진 소요 연수: 대리→과장 평균 **6.2년** (업종 평균 4.1년)
- 직무 순환 기회: 年 1회 미만 (타사 2~3회 대비 부족)
- 개인 경력 대화(Career Talk) 실시율: **연 21%** (형식적)

**[보상 공정성 저하 원인]**
- 동일 직급 내 연봉 편차: 최대 23% (원인 불투명)
- 성과급 산정 기준 비공개 → "블랙박스 보상" 인식

**[WLB 악화 원인]**
- 월 평균 초과 근무: 24시간 (전기 18시간 대비 +33%)
- 재택근무 일수 축소: 주 2일 → 1일로 縮小된 영향`,
      },
      {
        id: "Action_Plan", title: "핵심 실행 과제 (Action Items)",
        content: `| 우선순위 | 과제 | 예산 | 담당 | 기한 |
|---------|------|------|------|------|
| ★★★ | 승진 적체 해소: 연 1회→상시 승진 검토제 도입 | - | HR | 9/1 |
| ★★★ | 성과급 산정 기준 공개 (투명성 제고) | - | HR, CFO | 7/31 |
| ★★ | 개인 경력개발 면담 분기 1회 의무화 | 0.3억 | HR, 팀장 | 8월~ |
| ★★ | OT 상한 제도 도입 (월 40시간) | - | HR | 8/1 |
| ★★ | 재택근무 주 2일 복원 | - | CEO, HR | 즉시 |
| ★ | Pulse 조사 분기별 도입 (5분 짧은 설문) | 0.5억 | HR | 9월~ |
| ★ | 직무 순환 프로그램 연 2회 공정 공모 | 1.0억 | HR, 각 본부 | 연내 |`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 마일스톤 | 목표 지표 |
|------|---------|---------|
| 즉시 | 재택근무 주2일 복원, 성과급 기준 공개 공지 | 체감 신속 개선 |
| 7월 말 | OT 한도 시행, 경력개발 면담 의무화 공지 | - |
| 9월 | 분기 Pulse 조사 첫 실시, 직무순환 공모 | 점수 방향 확인 |
| 12월 | 상시 승진 검토제 첫 적용, 연간 만족도 조사 | **eNPS +28점 목표** |`,
      },
    ],
  },

  // ── 사건사고 보고 ─────────────────────────────────────────
  Incident_Report: {
    reportTitle: "[긴급] 개인정보 유출 사고 발생 보고 및 대응 현황",
    urgencyLevel: "RED",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 2026-04-17 14:00 고객 개인정보 유출 사고 최초 인지. 현재 임시 조치 완료, 추가 조사 진행 중.

**[현재 상황 (2026-04-17 18:00 기준)**
- 유출 추정 규모: 고객 약 2,400명 개인정보 (이름, 이메일, 전화번호)
- 유출 경로: 외부 해킹 시도에 의한 API 취약점 악용 (추정)
- 초동조치: 해당 API 엔드포인트 즉시 차단 완료 (15:30)

**[의사결정 요청]**
1. 개인정보보호위원회 72시간 내 신고 승인 (법적 의무)
2. 피해 고객 2,400명 통지 및 보상 방안 승인
3. 외부 보안 전문 업체 긴급 포렌식 의뢰 승인 (예산 약 3,000만 원)`,
      },
      {
        id: "Root_Cause_Analysis", title: "근본 원인 분석",
        content: `**[타임라인]**
- 14:00 이상 트래픽 감지 (SIEM 알림)
- 14:37 보안팀 확인 후 인지 (최초 알림 후 37분 지연)
- 15:30 해당 API 차단 및 임시 서비스 우회 조치 완료
- 17:00 피해 범위 1차 파악 완료

**[근본 원인 (5-Why 분석)]**
- 1차: API 취약점 악용됨
- 2차: 해당 API 보안 점검 未실시 (최근 6개월 내)
- 3차: 분기별 보안 점검 프로세스가 레거시 시스템에 미적용
- 4차: 레거시 시스템 관리 체계 미흡
- **근본 원인: 전사 보안 점검 프로세스의 커버리지 공백**`,
      },
      {
        id: "Risk_Mitigation", title: "리스크 및 대응 방안",
        content: `**[법적 리스크]**
- 개인정보보호법 위반 시: 최대 매출액 3% 과징금 (약 XX억 원)
- 72시간 내 신고 미이행 시: 별도 과태료

**[평판 리스크]**
- 언론 노출 시 브랜드 신뢰도 하락 가능성
- 사전 대응 필요: 고객 통지 + 공식 사과문 준비

**[즉각 대응 계획]**
| 과제 | 담당 | 기한 |
|------|------|------|
| 개인정보위 신고 | 법무팀, CPO | 72시간 내 (4/20까지) |
| 고객 개별 통지 | 고객서비스팀 | 4/19 완료 |
| 보안 포렌식 의뢰 | IT보안팀 | 4/18 즉시 |
| 공식 사과문 준비 | PR, 경영진 | 4/18 EOD |
| 보상 방안 확정 | CS, 법무 | 4/19 |`,
      },
      {
        id: "Action_Plan", title: "핵심 실행 과제 (Action Items)",
        content: `**[단기 — 48시간 내]**
- 법적 신고 완료, 고객 통지, 포렌식 착수, 보도자료 준비

**[중기 — 1개월 내]**
- 전 시스템 보안 점검 (레거시 포함)
- API 게이트웨이 전체 재검토 및 패치

**[장기 — 3개월 내]**
- 보안 거버넌스 체계 재수립
- 연 4회 정기 모의해킹(Penetration Test) 의무화
- 보안팀 인원 2명 충원 및 CISO 직급 신설 검토`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 사항 |
|------|------|
| 4/18 (D+1) | 포렌식 착수, 사과문 발표, 보안 점검 착수 |
| 4/20 (D+3) | 개인정보위 신고 완료, 고객 통지 완료 |
| 4/30 | 포렌식 결과 1차 보고, 보상 방안 확정 |
| 5/31 | 전 시스템 보안 점검 완료 보고 |
| 7/31 | 보안 거버넌스 체계 재수립 완료 |`,
      },
    ],
  },

  // ── 직장 내 괴롭힘 / 윤리 위반 ──────────────────────────
  Ethics_Violation: {
    reportTitle: "[긴급] 직장 내 괴롭힘 신고 사건 처리 결과 보고 및 의사결정 요청",
    urgencyLevel: "YELLOW",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 인사팀 익명 신고함 통해 A본부 B팀장의 직장 내 괴롭힘 신고 접수. 2주간 조사 결과 사실 확인됨.

**[핵심 사실]**
- 신고 접수: 2026-03-18 (익명 신고 2건 동시)
- 피신고인: B팀장 (재직 8년, 팀원 12명 관리)
- 피해자: 팀원 3명 (언어적 폭력, 업무 외 지시, 반복적 인격 비하)
- 조사 기간: 2026-03-20~04-03 (2주, 조사위원회 3인 구성)

**[의사결정 요청]**
1. 징계 수위 결정: [검토안] **정직 2개월 + 보직 해임** (아래 근거 참조)
2. 피해자 3명 지원 방안 승인 (부서 이동 희망 2명, 심리 상담 전원)`,
      },
      {
        id: "Root_Cause_Analysis", title: "근본 원인 분석",
        content: `**[조사위원회 결과 요약]**
- 사실로 확인된 행위:
  - 반복적 인격 비하 발언 (증언 일치, 동료 목격)
  - 퇴근 후 카카오톡 업무 지시 (메시지 기록 확보)
  - 개인 심부름 지시 3건 (목격자 2명)
- 근로기준법 76조의2 (직장 내 괴롭힘 금지) 해당

**[조직적 원인 진단]**
- B팀장 부임 이후 해당 팀 이직율: 연 29% (전사 평균 14%의 2배)
- HR의 조기 신호 감지 실패: 이직자 퇴직 인터뷰에서 관련 내용 언급 있었으나 미조치
- 익명 신고 채널 홍보 부족: 피해자 3인 모두 "신고 채널 몰랐다"고 진술`,
      },
      {
        id: "Risk_Mitigation", title: "리스크 및 대응 방안",
        content: `**[징계 수위 검토]**
| 옵션 | 내용 | 찬반 |
|------|------|------|
| A안 (권고) | 정직 2개월 + 보직 해임 | 사안 중대성 반영, 법적 방어 유리 |
| B안 | 감봉 3개월 (보직 유지) | 과도하다는 反 의견 가능 |
| C안 | 해고 | 법원 판례상 첫 적발에 과도할 수 있음 |

**[피해자 보호 조치]**
- 즉시: 심리 상담 전원 지원 (EAP 연계)
- 부서 이동 희망자 2명: 이달 내 이동 완료
- 인사상 불이익 없음 명문화

**[2차 피해 방지]**
- B팀장 정직 기간 중 피해자 직접 접촉 금지 통보`,
      },
      {
        id: "Action_Plan", title: "핵심 실행 과제 (Action Items)",
        content: `| No. | 과제 | 담당 | 기한 |
|-----|------|------|------|
| 1 | 징계위원회 소집 및 의결 | HR, 법무 | 4/25 |
| 2 | 피해자 부서 이동 완료 | HR | 4/30 |
| 3 | EAP 심리 상담 3인 연결 | HR | 즉시 |
| 4 | 전 관리자 대상 예방 교육 (필수) | HR | 5/31 |
| 5 | 익명 신고 채널 전 직원 재공지 | HR | 4/18 |
| 6 | 퇴직자 인터뷰 강화 프로세스 개선 | HR | 5/31 |`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 마일스톤 |
|------|---------|
| 4/18 | 신고 채널 전 직원 공지, EAP 상담 연결 |
| 4/25 | 징계위원회 개최 및 의결 |
| 4/30 | 피해자 부서 이동 완료, 징계 확정 통보 |
| 5/31 | 전 관리자 예방 교육 완료 |
| 6/30 | 재발 방지 시스템 구축 완료 보고 |`,
      },
    ],
  },

  // ── 기타 시나리오 Mock (기존 유지) ──────────────────────
  Org_Restructuring: {
    reportTitle: "영업·마케팅 본부 통합 조직 개편(안) 보고",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 영업본부 + 마케팅본부 → **GTM(Go-To-Market) 통합본부** 신설 승인 요청

**[개편 핵심]**
- 현황: 두 본부 간 캠페인·리드 정보 사일로화 → 영업 기회 손실 연 약 30억 원 추산
- 목표: 통합 데이터 파이프라인 + 단일 목표 KPI 체계로 마케팅→영업 전환율 2배 달성

**[의사결정 요청]** 2026년 7월 1일 GTM 통합본부 출범 승인`,
      },
      {
        id: "AS_IS_TO_BE", title: "조직 구조 비교 (AS-IS / TO-BE)",
        content: `**개편 전후 조직 구조 비교**`,
        asIsToBeTable: true,
        asIsData: {
          label: "조직 구조",
          asList: ["영업본부 (본부장 A, 팀원 45명)", "마케팅본부 (본부장 B, 팀원 22명)", "독립 운영, 별개 KPI"],
          toBeList: ["GTM 통합본부 (통합 본부장 1명, 총 67명)", "영업팀 + 마케팅팀 + 데이터팀 3개팀 구성", "통합 전환율 KPI 단일화"],
        },
      },
      {
        id: "Risk_Mitigation", title: "리스크 및 대응 방안",
        content: `**[주요 리스크]**
- 본부장 2명 → 1명: 1인 보직 해소에 따른 반발 예상
- 통합 초기 혼선으로 일시적 성과 하락 (전환기 3개월)

**[대응]**
- 기존 마케팅 본부장 B: 전략기획 담당 Executive 직책 신설 전환
- 통합 TF 운영 (6월~7월): 두 팀 Joint 운영으로 연착륙`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 단계 | 내용 | 기한 |
|------|------|------|
| 1 | 사전 개별 면담 (본부장 2명) | 4/25까지 |
| 2 | 팀원 대상 설명회 개최 | 5/15 |
| 3 | 통합 TF 구성 및 운영 | 6/1~6/30 |
| 4 | GTM 통합본부 공식 출범 | **7/1** |`,
      },
    ],
  },

  Resource_Plan: {
    reportTitle: "2026년 하반기 인력 운영 계획",
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** 하반기 인력 배치 효율화를 통해 신규 채용 없이 사업 목표 달성 가능 — 승인 요청

**[핵심 내용]**
- 현황 비효율: 개발팀 가동률 118% (과부하) vs 기획팀 74% (유휴)
- 대안: 기획팀→개발팀 3명 한시 지원 파견 (6개월)
- 효과: 개발팀 납기 준수율 73% → 92% 개선 예상, 추가 채용비 절감 约 1.2억 원`,
      },
      {
        id: "Resource_Allocation", title: "자원 배분 계획",
        content: `**[현행 vs 개편 인력 배치]**
| 팀 | 현재 인원 | 현재 가동률 | 조정 후 | 목표 가동률 |
|----|---------|-----------|--------|-----------|
| 개발팀 | 15명 | 118% | 18명 | 95% |
| 기획팀 | 12명 | 74% | 9명 | 92% |

3명 한시 파견 기간: 2026년 7월~12월 (6개월)`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `| 시점 | 내용 |
|------|------|
| 6월 말 | 파견 대상자 확정, 개별 면담 |
| 7월 1일 | 파견 발령 |
| 9월 말 | 중간 점검 (가동률, 만족도) |
| 12월 말 | 성과 평가 후 연장 또는 원소속 복귀 결정 |`,
      },
    ],
  },

  // Custom_Issue는 항상 AI가 생성 (Mock 없음)
  Custom_Issue: null,
};

// ─────────────────────────────────────────────────────────
// 메인 API 함수
// ─────────────────────────────────────────────────────────

/**
 * ④ 보고서 생성 메인 함수 (tone 파라미터 추가)
 * @param {string} scenarioKey - 선택된 시나리오 키
 * @param {object} formData    - 사용자 입력 데이터
 * @param {string} tone        - 보고서 문체 ("samsung" | "bluehouse" | "startup" | "global")
 * @returns {Promise<object>}  - 생성된 보고서 데이터
 */
export async function generateReport(scenarioKey, formData, tone = "samsung") {
  const hasApiKey = !!import.meta.env.VITE_GEMINI_API_KEY;

  if (hasApiKey) {
    // 실제 Gemini API 호출 경로 (tone 전달)
    return await callGeminiAPI(scenarioKey, formData, tone);
  } else {
    // Mock 모드: API Key 없이 즉시 데모 데이터 반환
    // tone 정보를 메타데이터로 포함하여 반환
    const result = await getMockResponse(scenarioKey, formData);
    return { ...result, tone };
  }
}

/**
 * ⑤ 섹션(블록) 재생성 함수
 * 특정 블록 ID의 내용만 AI로 재생성합니다.
 * 비유: 보고서의 1페이지만 다시 써달라고 요청하는 것
 * @param {string} scenarioKey  - 시나리오 키
 * @param {string} blockId      - 재생성할 블록 ID
 * @param {string} blockTitle   - 재생성할 블록 제목
 * @param {object} formData     - 원본 사용자 입력 데이터
 * @param {string} tone         - 보고서 문체
 * @returns {Promise<string>}   - 새로 생성된 블록 content 텍스트
 */
export async function regenerateBlock(scenarioKey, blockId, blockTitle, formData, tone = "samsung") {
  const hasApiKey = !!import.meta.env.VITE_GEMINI_API_KEY;

  if (hasApiKey) {
    // 실제 Gemini API 호출 — 해당 블록만 재작성 요청
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
    const systemPrompt = buildSystemPrompt(tone);

    // 원본 입력데이터 요약
    const contextText = Object.entries(formData)
      .map(([k, v]) => `- ${k}: ${v}`)
      .join("\n");

    const prompt = `
다음 보고서의 "${blockTitle}" 섹션만 새로운 관점으로 재작성해 주세요.

[원본 입력 데이터]
${contextText}

[재작성 지침]
- 섹션 제목: ${blockTitle}
- 기존 내용과 다른 표현/구조/관점을 사용할 것
- 마크다운 형식 유지 (**굵게**, 표, 리스트 등)
- JSON이 아닌 순수 텍스트로만 반환
`;

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system_instruction: { parts: [{ text: systemPrompt }] },
          contents: [{ role: "user", parts: [{ text: prompt }] }],
          generationConfig: { temperature: 0.7, maxOutputTokens: 1024 },
        }),
      });
      const data = await response.json();
      return data.candidates?.[0]?.content?.parts?.[0]?.text || "재생성에 실패했습니다.";
    } catch (err) {
      console.error("블록 재생성 오류:", err);
      return "재생성 중 오류가 발생했습니다. 다시 시도해 주세요.";
    }
  } else {
    // Mock 모드: 1초 딜레이 후 변형된 샘플 텍스트 반환
    await new Promise((r) => setTimeout(r, 1000));
    return `**[재작성된 ${blockTitle}]**\n\n이 섹션은 AI가 새로운 관점으로 재작성했습니다.\n\n**[핵심 요점]**\n- 실제 Gemini API 연결 시 완전한 재생성이 이루어집니다.\n- 현재는 Mock 모드로 동작 중입니다.\n\n**[변경 포인트]**\n- 기존 내용과 다른 구조와 표현 방식을 적용했습니다.\n- 추가적인 수치 분석 및 비교 데이터를 포함했습니다.`;
  }
}

/**
 * Mock 응답 함수 (개발/데모용)
 */
async function getMockResponse(scenarioKey, formData) {
  // 실제 네트워크 지연처럼 체감하도록 인위적 딜레이
  await new Promise((resolve) => setTimeout(resolve, 1800));

  const mockData = MOCK_RESPONSES[scenarioKey];

  if (!mockData) {
    // Custom_Issue 또는 Mock 없는 케이스 → AI 설계 시뮬레이션
    return generateCustomMockReport(formData);
  }

  // 사용자 입력 키워드를 보고서 제목에 반영 (보다 개인화된 느낌)
  const firstAnswer = Object.values(formData)[0] || "";
  const personalizedTitle = firstAnswer
    ? `${mockData.reportTitle} (기반: "${firstAnswer.slice(0, 30)}...")`
    : mockData.reportTitle;

  return {
    ...mockData,
    reportTitle: personalizedTitle,
    generatedAt: new Date().toISOString(),
    isMock: true,
  };
}

/**
 * Custom_Issue: AI가 목차를 직접 설계하는 시뮬레이션
 */
function generateCustomMockReport(formData) {
  const keyword = formData["q_keyword"] || "현안 보고";
  const purpose = formData["q1"] || "의사결정 지원";

  return {
    reportTitle: `[기획 보고서] ${keyword}`,
    blocks: [
      {
        id: "Executive_Summary", title: "요약 및 의사결정 요청",
        content: `**[결론]** ${keyword} 관련 현안 보고 및 의사결정 요청

**[보고 배경]**
${purpose}

**[핵심 요청 사항]**
- AI가 입력하신 키워드를 분석하여 최적 보고서 목차를 설계했습니다.
- 실제 VITE_GEMINI_API_KEY 설정 후 Gemini AI가 완전한 보고서를 작성합니다.`,
      },
      {
        id: "Root_Cause_Analysis", title: "현황 및 문제 진단",
        content: `**[현황]**
${keyword}에 대한 현황 분석 결과, 구조적 개입이 필요한 시점으로 판단됨.

**[문제 진단]**
- 핵심 원인 1: (AI가 자동 분석 — API Key 설정 후 활성화)
- 핵심 원인 2: (AI가 자동 분석)
- 근본 원인: (5-Why 기반 진단)`,
      },
      {
        id: "Risk_Mitigation", title: "리스크 및 대응 방안",
        content: `**[예상 리스크]**
- 현안 미해결 시 발생 가능한 리스크 (AI 분석 예정)

**[대응 방안]**
- 단기 대응, 중기 대응, 장기 대응 3단계 로드맵 (API Key 설정 후 Gemini AI 생성)`,
      },
      {
        id: "Action_Plan", title: "핵심 실행 과제 (Action Items)",
        content: `| No. | 과제 | 담당 | 기한 | 예산 |
|-----|------|------|------|------|
| 1 | (Gemini AI 자동 생성) | TBD | TBD | TBD |
| 2 | (Gemini AI 자동 생성) | TBD | TBD | TBD |`,
      },
      {
        id: "Timeline", title: "추진 일정",
        content: `(Gemini API Key 설정 후 AI가 현안에 최적화된 일정을 자동 생성합니다.)`,
      },
    ],
    generatedAt: new Date().toISOString(),
    isMock: true,
  };
}

/**
 * 실제 Gemini API 호출 함수
 */
async function callGeminiAPI(scenarioKey, formData, tone = "samsung") {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
  const endpoint = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;

  // 사용자 입력 → 텍스트 변환
  const userInputText = Object.entries(formData)
    .map(([key, value]) => `- ${key}: ${value}`)
    .join("\n");

  // Custom_Issue는 메타 프롬프팅 사용
  const isCustom = scenarioKey === "Custom_Issue";
  const userPrompt = isCustom
    ? buildMetaPrompt(formData)
    : buildSectionPrompt(scenarioKey, formData);

  // ④ 선택된 톤에 맞는 시스템 프롬프트 사용
  const systemPrompt = buildSystemPrompt(tone);

  const requestBody = {
    system_instruction: { parts: [{ text: systemPrompt }] },
    contents: [{ role: "user", parts: [{ text: userPrompt }] }],
    generationConfig: { temperature: 0.4, maxOutputTokens: 4096 },
  };

  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`API 오류: ${response.status}`);
    }

    const data = await response.json();
    const rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || "";

    // JSON 파싱 시도
    const jsonMatch = rawText.match(/```json\n?([\s\S]*?)\n?```/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch[1]);
    }

    // 파싱 실패 시 단순 텍스트 래핑
    return {
      reportTitle: "AI 생성 보고서",
      blocks: [{ id: "raw", title: "AI 생성 내용", content: rawText }],
      generatedAt: new Date().toISOString(),
      isMock: false,
    };
  } catch (error) {
    console.error("Gemini API 호출 오류:", error);
    // 오류 시 Mock으로 폴백
    return getMockResponse(scenarioKey, formData);
  }
}

/**
 * 일반 시나리오 프롬프트 빌더
 */
function buildSectionPrompt(scenarioKey, formData) {
  const answers = Object.entries(formData)
    .map(([k, v]) => `Q(${k}): ${v}`)
    .join("\n");

  return `
다음은 "${scenarioKey}" 유형의 C-Level 보고서 작성을 위한 사용자 입력입니다.

[사용자 입력]
${answers}

[요청]
위 내용을 바탕으로 C-Level 보고서를 JSON 형식으로 생성해 주세요.
형식은 다음과 같습니다:
\`\`\`json
{
  "reportTitle": "보고서 제목",
  "blocks": [
    {
      "id": "Executive_Summary",
      "title": "요약 및 의사결정 요청",
      "content": "마크다운 형식의 보고서 내용"
    }
  ]
}
\`\`\`
`;
}

/**
 * Custom_Issue 메타 프롬프팅 빌더
 */
function buildMetaPrompt(formData) {
  const keyword = formData["q_keyword"] || "";
  const purpose = formData["q1"] || "";
  const data = formData["q2"] || "";
  const alternatives = formData["q3"] || "";

  return `
당신은 전략기획 보고서 전문가입니다.
다음 현안에 대해 C-Level 보고용 최적 목차를 AI가 직접 설계하고 각 섹션의 내용을 작성해 주세요.

[핵심 주제] ${keyword}
[의사결정 목적] ${purpose}
[관련 데이터/근거] ${data}
[검토 대안] ${alternatives}

[지침]
1. BLUF 원칙 적용: 결론과 요청 사항을 첫 번째 블록에 배치
2. MECE 원칙: 누락·중복 없는 논리 구조
3. 개조식 문체: "~임", "~함" 사용
4. 핵심 비교는 표(마크다운 테이블) 형식 사용
5. 제일 마지막 블록은 반드시 추진 일정 포함

JSON 형식으로 보고서를 생성해 주세요:
\`\`\`json
{
  "reportTitle": "보고서 제목",
  "blocks": [
    { "id": "Executive_Summary", "title": "요약 및 의사결정 요청", "content": "내용" },
    ...
  ]
}
\`\`\`
`;
}
